import { useEffect } from 'react';
import { Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';

import PortfolioReview from '~/features/portfolioReview';
import { PortfolioContextProvider } from '~/hooks/providers/PortfolioContextProvider';
import PortfolioPage from '~/components/PortfolioPage';
import RedirectRoute from './RedirectRoute';
import Messaging from '~/features/messaging';
import { MessagingProvider } from '~/features/messaging/providers/MessagingProvider';
import { ConversationProvider } from '~/features/messaging/providers/ConversationProvider';
import { validation } from '~/schema/portfolio';
import { fetchPortfolio, updatePortfolio } from '~/api/portfolios';
import { AutoSaveContextProvider } from '~/hooks/providers/AutoSaveContextProvider';
import TeamManagement from '~/components/TeamManagement';
import Dataroom from '~/features/dataroom';
import { DataroomProvider } from '~/features/dataroom/providers/DataroomProvider';
import PortfolioBidding from '~/features/portfolioBidding';
import { MarketplaceControls } from '~/features/marketplace/admin/MarketplaceControls';
import { usePermissions } from '~/requests/permissions/usePermissions';
import MarketplaceReview from '~/features/marketplace/MarketplaceReview';
import { logUserActivity, UserActivityTypes } from '~/api/user';

function Portfolio() {
  const { id: portfolioId } = useParams() as any;
  const history = useHistory();
  const location = useLocation();
  const { permissions } = usePermissions();

  useEffect(() => {
    if (portfolioId) {
      logUserActivity({ activityType: UserActivityTypes.PORTFOLIO_VIEW, portfolioId });
    }
  }, [portfolioId]);

  return (
    <AutoSaveContextProvider
      id={portfolioId}
      validation={validation}
      label='portfolio'
      api={{
        update: updatePortfolio, fetch(id: any) {
          return fetchPortfolio(id).then((data) => {
            // Redirect to different project if needed
            if (data.redirectToPortfolioId && portfolioId !== data.redirectToPortfolioId && location.pathname.includes(`/portfolio/${portfolioId}`)) {
              const newPathname = location.pathname.replace(`/portfolio/${portfolioId}`, `/portfolio/${data.redirectToPortfolioId}`);
              history.replace(newPathname);
            }
            return data;
          });
        }
      }}
    >
      <PortfolioContextProvider>
        <PortfolioPage>
          <Switch>
            <Route path="/portfolio/:id/review">
              <PortfolioReview />
            </Route>
            <Route path="/portfolio/:id/bidding">
              <PortfolioBidding />
            </Route>
            <Route path="/portfolio/:id/messages">
              <ConversationProvider>
                <div className="card">
                  <MessagingProvider pageType="portfolio">
                    <Messaging />
                  </MessagingProvider>
                </div>
              </ConversationProvider>
            </Route>
            <Route exact path="/portfolio/:id/team">
              <div className="card">
                <TeamManagement />
              </div>
            </Route>
            <Route exact path="/portfolio/:id/dataroom">
              <DataroomProvider>
                <div className="card">
                  <Dataroom />
                </div>
              </DataroomProvider>
            </Route>
            <Route exact path="/portfolio/:id">
              <RedirectRoute to={`/portfolio/${portfolioId}/review`} />
            </Route>
            <Route exact path="/portfolio/:id/listing">
              <div className="card">
                {permissions.isAdmin ? (
                  <MarketplaceControls portfolioId={portfolioId} />
                ) : permissions.isDeveloper ? (
                  <MarketplaceReview portfolioId={portfolioId} />
                ) : (
                  <RedirectRoute to={`/portfolio/${portfolioId}/review`} />
                )}
              </div>
            </Route>
          </Switch>
        </PortfolioPage>
      </PortfolioContextProvider>
    </AutoSaveContextProvider>
  );
}

export default Portfolio;
